import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Database,
  TrendingUp,
  Lock,
  Plus,
  Trash2,
  X,
  FileText,
  Loader2,
  Download,
  Share2,
  Link,
  Filter,
} from "lucide-react";
import { categoryIcons } from "../lib/constants";
import { useApi } from "../hooks/useApi";
import { DashboardLayout } from "../components/DashboardLayout";

export const VaultPage = () => {
  const api = useApi();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    description: "",
    data_content: "",
    is_for_sale: false,
  });

  const loadData = useCallback(async () => {
    try {
      const [itemsRes, catRes] = await Promise.all([
        api.get(selectedCategory ? `/data?category=${selectedCategory}` : "/data"),
        api.get("/categories"),
      ]);
      setItems(itemsRes.data);
      setCategories(catRes.data);
    } catch (e) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [api, selectedCategory]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/data", formData);
      toast.success("Data added successfully!");
      setShowModal(false);
      setFormData({ name: "", category: "", description: "", data_content: "", is_for_sale: false });
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add data");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this data?")) return;
    try {
      await api.delete(`/data/${id}`);
      toast.success("Data deleted");
      loadData();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const toggleForSale = async (item) => {
    try {
      await api.put(`/data/${item.id}`, { is_for_sale: !item.is_for_sale });
      toast.success(item.is_for_sale ? "Removed from marketplace" : "Listed on marketplace");
      loadData();
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const openShareModal = (item) => {
    setSelectedItem(item);
    setShowShareModal(true);
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto" data-testid="vault-page">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">Data Vault</h1>
            <p className="text-gray-400 mt-1">Securely store and manage your personal data</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-purple-600 to-cyan-500 text-white rounded-xl font-medium hover:shadow-lg hover:shadow-purple-500/30 transition"
            data-testid="add-data-btn"
          >
            <Plus className="w-5 h-5" /> Add Data
          </button>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => setSelectedCategory("")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition ${
              !selectedCategory
                ? "bg-gradient-to-r from-purple-600 to-cyan-500 text-white"
                : "bg-white/10 text-gray-400 hover:bg-white/20 border border-purple-500/20"
            }`}
          >
            All
          </button>
          {categories.map((cat) => (
            <button
              key={cat.name}
              onClick={() => setSelectedCategory(cat.name)}
              className={`px-4 py-2 rounded-full text-sm font-medium capitalize transition ${
                selectedCategory === cat.name
                  ? "bg-gradient-to-r from-purple-600 to-cyan-500 text-white"
                  : "bg-white/10 text-gray-400 hover:bg-white/20 border border-purple-500/20"
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>

        {/* Data Items */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-12 text-center border border-purple-500/20">
            <Database className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-300 mb-2">No data yet</h3>
            <p className="text-gray-500 mb-6">Start by adding your first data item to the vault</p>
            <button
              onClick={() => setShowModal(true)}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-cyan-500 text-white rounded-xl font-medium hover:shadow-lg transition"
            >
              Add Your First Data
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((item) => {
              const Icon = categoryIcons[item.category] || FileText;
              return (
                <div
                  key={item.id}
                  className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 hover:shadow-lg hover:shadow-purple-500/10 transition border border-purple-500/20"
                  data-testid={`data-item-${item.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/20 to-cyan-500/20 flex items-center justify-center border border-purple-500/30">
                        <Icon className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-white">{item.name}</h3>
                        <p className="text-sm text-gray-400 capitalize">{item.category}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Lock className="w-4 h-4 text-green-400" />
                    </div>
                  </div>
                  {item.description && (
                    <p className="text-gray-400 text-sm mb-4 line-clamp-2">{item.description}</p>
                  )}
                  <div className="flex items-center justify-between pt-4 border-t border-purple-500/20">
                    <div>
                      <p className="text-lg font-bold text-white">${item.estimated_value?.toFixed(2)}</p>
                      <p className="text-xs text-gray-500">Estimated Value</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openShareModal(item)}
                        className="p-2 rounded-lg bg-white/10 text-gray-400 hover:bg-blue-500/20 hover:text-blue-400 transition"
                        title="Share"
                      >
                        <Share2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => toggleForSale(item)}
                        className={`p-2 rounded-lg transition ${
                          item.is_for_sale
                            ? "bg-green-500/20 text-green-400"
                            : "bg-white/10 text-gray-400 hover:bg-white/20"
                        }`}
                        title={item.is_for_sale ? "Listed" : "List for sale"}
                      >
                        <TrendingUp className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="p-2 rounded-lg bg-white/10 text-gray-400 hover:bg-red-500/20 hover:text-red-400 transition"
                        data-testid={`delete-${item.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Add Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-900 rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto border border-purple-500/20" data-testid="add-data-modal">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Add New Data</h2>
                <button onClick={() => setShowModal(false)} className="p-2 hover:bg-white/10 rounded-lg text-gray-400">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-purple-500/30 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none text-white placeholder-gray-500"
                    placeholder="e.g., Shopping Preferences"
                    required
                    data-testid="data-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-purple-500/30 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none text-white"
                    required
                    data-testid="data-category-select"
                  >
                    <option value="" className="bg-slate-900">Select a category</option>
                    {categories.map((cat) => (
                      <option key={cat.name} value={cat.name} className="bg-slate-900">
                        {cat.name.charAt(0).toUpperCase() + cat.name.slice(1)} - {cat.description}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Description (optional)</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-purple-500/30 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none text-white placeholder-gray-500"
                    placeholder="Brief description of your data"
                    data-testid="data-description-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Data Content</label>
                  <textarea
                    value={formData.data_content}
                    onChange={(e) => setFormData({ ...formData, data_content: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-purple-500/30 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none h-32 resize-none text-white placeholder-gray-500"
                    placeholder="Enter or paste your data here..."
                    required
                    data-testid="data-content-input"
                  />
                  <p className="text-xs text-cyan-400 mt-1">Your data will be encrypted with AES-256</p>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="forSale"
                    checked={formData.is_for_sale}
                    onChange={(e) => setFormData({ ...formData, is_for_sale: e.target.checked })}
                    className="w-4 h-4 text-purple-500 rounded"
                  />
                  <label htmlFor="forSale" className="text-sm text-gray-300">
                    List on marketplace for potential buyers
                  </label>
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 py-3 border border-purple-500/30 rounded-xl font-medium text-gray-300 hover:bg-white/5 transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-cyan-500 text-white rounded-xl font-medium hover:shadow-lg transition"
                    data-testid="submit-data-btn"
                  >
                    Add Data
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Share Modal */}
        {showShareModal && selectedItem && (
          <ShareModal item={selectedItem} onClose={() => setShowShareModal(false)} />
        )}
      </div>
    </DashboardLayout>
  );
};

// Share Modal Component
export const ShareModal = ({ item, onClose }) => {
  const api = useApi();
  const [shareEmail, setShareEmail] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [permissionType, setPermissionType] = useState("view");
  const [expiresInDays, setExpiresInDays] = useState("");
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState([]);

  const loadPermissions = useCallback(async () => {
    try {
      const res = await api.get("/sharing");
      setPermissions(res.data.filter(p => p.data_item_id === item.id));
    } catch (e) {
      console.error("Failed to load permissions");
    }
  }, [api, item.id]);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  const handleShare = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/sharing", {
        data_item_id: item.id,
        shared_with_email: shareEmail || null,
        permission_type: permissionType,
        is_public: isPublic,
        expires_in_days: expiresInDays ? parseInt(expiresInDays) : null,
      });
      toast.success("Sharing permission created");
      setShareEmail("");
      loadPermissions();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to share");
    } finally {
      setLoading(false);
    }
  };

  const revokePermission = async (id) => {
    try {
      await api.delete(`/sharing/${id}`);
      toast.success("Permission revoked");
      loadPermissions();
    } catch (e) {
      toast.error("Failed to revoke");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto border border-purple-500/20">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Share "{item.name}"</h2>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg text-gray-400">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleShare} className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Share with (email)</label>
            <input
              type="email"
              value={shareEmail}
              onChange={(e) => setShareEmail(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-white/10 border border-purple-500/30 focus:ring-2 focus:ring-purple-500 outline-none text-white placeholder-gray-500"
              placeholder="someone@example.com (leave empty for link sharing)"
              disabled={isPublic}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Permission Type</label>
            <select
              value={permissionType}
              onChange={(e) => setPermissionType(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-white/10 border border-purple-500/30 focus:ring-2 focus:ring-purple-500 outline-none text-white"
            >
              <option value="view" className="bg-slate-900">View Only</option>
              <option value="download" className="bg-slate-900">View & Download</option>
              <option value="full" className="bg-slate-900">Full Access</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Expires In (days)</label>
            <input
              type="number"
              value={expiresInDays}
              onChange={(e) => setExpiresInDays(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-white/10 border border-purple-500/30 focus:ring-2 focus:ring-purple-500 outline-none text-white placeholder-gray-500"
              placeholder="Leave empty for no expiration"
              min="1"
            />
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="isPublic"
              checked={isPublic}
              onChange={(e) => {
                setIsPublic(e.target.checked);
                if (e.target.checked) setShareEmail("");
              }}
              className="w-4 h-4 text-purple-500 rounded"
            />
            <label htmlFor="isPublic" className="text-sm text-gray-300">
              Make publicly accessible (anyone with link)
            </label>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-gradient-to-r from-purple-600 to-cyan-500 text-white rounded-xl font-medium hover:shadow-lg transition disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Share2 className="w-5 h-5" />}
            Create Share Link
          </button>
        </form>

        {/* Existing Permissions */}
        {permissions.length > 0 && (
          <div>
            <h3 className="font-medium text-gray-300 mb-3">Active Permissions</h3>
            <div className="space-y-2">
              {permissions.map((perm) => (
                <div key={perm.id} className="flex items-center justify-between p-3 bg-white/5 rounded-xl border border-purple-500/10">
                  <div className="flex-1">
                    <p className="text-white text-sm">
                      {perm.is_public ? "Public Link" : perm.shared_with_email || "Anyone with link"}
                    </p>
                    <p className="text-gray-500 text-xs capitalize">{perm.permission_type} access</p>
                  </div>
                  <button
                    onClick={() => revokePermission(perm.id)}
                    className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// AI Valuation Page
